import { OrganizationBankAccountDTOAccountTypeEnum } from '@reposit/api-client';
import { startCase, toLower } from 'lodash';
import moment from 'moment';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { ExpandableContent } from '../../components/ExpandableContent';
import InlineEdit from '../../components/InlineEdit';
import InlineTable, { InlineTableItem } from '../../components/InlineTable';
import { OrganizationBankAccountEntity } from '../../redux/entities/entities.types';
import BankAccountHeader from './bank-account-header';
import EditVerificationNameModal from './edit-verification-name.modal';

const Container = styled.div`
  padding-bottom: 24px;
`;

const VerificationNameValue = styled.span`
  display: inline;
`;

const SORT_CODE_REGEX = /(\d)(?=(\d{2})+(?!\d))/g;

interface BankAccountProps {
  bankAccount: OrganizationBankAccountEntity;
}

const getBankAccountType = (accountType: OrganizationBankAccountDTOAccountTypeEnum) => {
  switch (accountType) {
    case OrganizationBankAccountDTOAccountTypeEnum.DEFAULT:
      return 'Primary';
    case OrganizationBankAccountDTOAccountTypeEnum.CLAIM:
      return 'Claim';
    case OrganizationBankAccountDTOAccountTypeEnum.CASHDEPOSITSCHEME:
      return 'Cash Deposit Scheme';
    default:
      return 'Unknown';
  }
};

const BankAccount: React.FC<BankAccountProps> = ({ bankAccount }) => {
  const [isEditingVerificationName, setIsEditingVerificationName] = useState(false);
  const canEditVerificationName = !bankAccount.deletedAt;
  const closeVerificationNameModal = useCallback(() => setIsEditingVerificationName(false), []);

  const items: InlineTableItem[] = [
    {
      key: 'Account Type',
      value: getBankAccountType(bankAccount.accountType),
    },
    { key: 'Bank Name', value: startCase(toLower(bankAccount.bankName)) },
    { key: 'Sort Code', value: bankAccount.sortCode.replace(SORT_CODE_REGEX, '$1-') },
    { key: 'Account Number', value: bankAccount.accountNumber },
    {
      key: 'Verification Name',
      value: (
        <VerificationNameValue>
          {bankAccount.verificationName || 'Not set'}
          {canEditVerificationName ? <InlineEdit onClick={() => setIsEditingVerificationName(true)} /> : null}
        </VerificationNameValue>
      ),
    },
    { key: 'Created At', value: moment(bankAccount.createdAt).format('DD/MM/YYYY') },
    { key: 'Deleted At', value: bankAccount.deletedAt ? moment(bankAccount.deletedAt).format('DD/MM/YYYY') : 'na' },
  ];

  return (
    <Container>
      {isEditingVerificationName ? (
        <EditVerificationNameModal
          bankAccountId={bankAccount.id}
          onDismiss={closeVerificationNameModal}
          organizationId={bankAccount.organizationId}
          verificationName={bankAccount.verificationName}
        />
      ) : null}
      <ExpandableContent header={<BankAccountHeader bankAccount={bankAccount} />} hiddenContent={<InlineTable items={items} />} />
    </Container>
  );
};

export default BankAccount;
